/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable no-unneeded-ternary */
import { useWasteStore } from '@waste/store/store';

export const WastePayload = () => {
  const {
    firstName,
    lastName,
    email,
    receiveConfirmationEmail,
    locationOfWaste,
    filename,
    blobUri,
    canYouGiveMoreDetail,
    anythingElseWeNeedToKnow,
    wasteCategory,
    currentSubcategory,
    surfaceDetails,
    actualMeasurementDetails,
    offensive,
    renderedQuestions,
  } = useWasteStore();

  const {
    address_line_1,
    address_line_2,
    town_city,
    county,
    postcode,
    country,
    UPRN,
    USRN,
    latitude,
    longitude,
  } = locationOfWaste;

  const getFormattedCategory = (category: string | undefined) => {
    // Replace hyphen between characters/numbers with underscore
    category = category?.replace(/([\w\d])-([\w\d])/g, '$1_$2');
    // Define URL sensitive characters, spaces, and other special characters as a RegExp pattern.
    const pattern = /[^A-Za-z0-9\s_-]/g;
    category = category?.replace(pattern, '');
    category = category?.replace(/\s+/g, '_').toLowerCase();
    // Replace multiple underscores with a single one
    category = category?.replace(/_{2,}/g, '_');
    // Remove trailing underscores
    return category?.replace(/_$/g, '');
  };

  const formattedCategory = getFormattedCategory(wasteCategory);

  const formattedCurrentSubcategory = getFormattedCategory(
    currentSubcategory[0]?.name,
  );

  const formattedActualMeasurementDetails = getFormattedCategory(
    actualMeasurementDetails,
  );

  const formattedSurfaceDetails = getFormattedCategory(surfaceDetails);

  // Replace bin category with it subcategory
  const getCategoryOrBinsSubcategory = () => {
    if (isBinsAndRenderedQuestionsExist()) {
      return formattedCurrentSubcategory;
    }
    return formattedCategory;
  };

  // Set the selected option for street cleaning or dead animal as a subcategory
  const getAllSubCategoryAsInDynamics = () => {
    if (isDeadAnimalsOrStreetCleaning()) {
      return formattedActualMeasurementDetails;
    } else if (isBinsAndRenderedQuestionsExist()) {
      return getFormattedCategory(renderedQuestions![0]?.response);
    }
    return formattedCurrentSubcategory;
  };

  // Set damage dirty or missing bin option as subcategory 2
  const getAllSubCategoryTwoAsInDynamics = () => {
    if (isBinsAndRenderedQuestionsExist()) {
      return secondQuestionResponse() ?? '';
    }
    return '';
  };

  // Set actual measurement details for categories with measurements values
  const getActualMeasurementDetails = () => {
    if (isDeadAnimalsOrStreetCleaning() || isBinsAndRenderedQuestionsExist()) {
      return '';
    } else if (
      formattedCategory === 'graffiti_posters_and_stickers' &&
      secondQuestionResponse()
    ) {
      return secondQuestionResponse();
    }
    return formattedActualMeasurementDetails;
  };

  const isDeadAnimalsOrStreetCleaning = () => {
    return (
      formattedCategory === 'dead_animals' ||
      formattedCategory === 'street_cleaning'
    );
  };

  const isBinsAndRenderedQuestionsExist = () => {
    return formattedCategory === 'bins' && renderedQuestions !== undefined;
  };

  const secondQuestionResponse = () => {
    if (renderedQuestions !== undefined && renderedQuestions.length >= 1) {
      return getFormattedCategory(renderedQuestions[1]?.response);
    }
  };

  const payload = {
    InteractionTransactionDetailsObject: {
      TypeDetailsObject: {
        code: '',
        description: '',
      },
      ReceiptDetailsObject: {
        submission_date: new Date().toISOString(),
        channel: 'Online',
        platform: 'React',
      },
    },
    PrimaryConsumerDetailsObject: {
      ConsumerIdDetailsObject: {
        id: '',
        token: '',
      },
      NameDetailsObject: {
        first_name: firstName,
        last_name: lastName,
      },
      ...(email !== '' && {
        ContactEmailDetailsObject: [
          {
            type: 'H',
            email_address: email,
          },
        ],
      }),
    },
    ServiceRegistrationDetailsObject: {
      anonymous: false,
      service_agreement: false,
      consumer_contact_primary:
        receiveConfirmationEmail.toLowerCase() === 'yes' ? true : false, // radio buttons can we contact you in location page (boolean value)
      service_location: 'Primary',
    },
    FileDetailsObject: [
      {
        name: filename, // image name
        path: blobUri, // blobImage
      },
    ],
    ServiceIncidentDetailsObject: {
      CoreServiceDetailsObject: {
        offensive, // Applicable only for Graffiti - true if yes else false
        IncidentDescriptionObject: {
          description1: canYouGiveMoreDetail, // Optional Can you give us more details to help us find it
          description2: anythingElseWeNeedToKnow, // Optional Is there anything else we need to know
        },
      },
      CategoryDetailsObject: {
        ActualCategoryDetailsObject: {
          category: getCategoryOrBinsSubcategory(),
          // Main category from the landing page or Bins Subcategory which replaces bins category in dynamics.
          subcategory1: getAllSubCategoryAsInDynamics(), // subcategory as in dynamics.
          subcategory2: getAllSubCategoryTwoAsInDynamics(), // subcategory 2 optional
        },
      },
      MeasurementDetailsObject: {
        ActualMeasurementDetailsObject: {
          description: getActualMeasurementDetails(),
          // formattedActualMeasurementDetails, // from how big is it question dropdown in location page
        },
      },
      SurfaceDetailsObject: {
        description: formattedSurfaceDetails, // from on what surface is it on in location page
      },
    },
    ServiceDeliveryLocationDetailsObject: {
      ServiceLocationDetailsObject: {
        address_line_1,
        address_line_2,
        town_city,
        county,
        postcode,
        country,
        UPRN,
        USRN,
        latitude: Number.parseFloat(latitude),
        longitude: Number.parseFloat(longitude),
      },
    },
  };

  return payload;
};
